import { AlertTriangle, ShieldAlert, Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import type { DangerousQueryResult } from '../../../../core/engines';

interface DangerousQueryDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  dangerInfo: DangerousQueryResult;
  /**
   * Where the statement would run. Load-bearing on the agent
   * execute-with-confirm path: under an "Always this session" grant there is
   * no confirmation toast, so this dialog is the only place the user can see
   * whether they are about to hit a scratch database or production.
   */
  connectionName?: string;
  database?: string;
}

/**
 * Generate an AI-like explanation message based on the dangerous query result.
 * The message should feel conversational and helpful, explaining why confirmation is needed.
 */
/**
 * Marker shared with `isAgentDestructiveQuery`. Agent-authored statements get
 * their own copy because under an "Always this session" grant this dialog is
 * the only UI the user sees — falling through to the generic wording would
 * leave them unaware they are reviewing something they did not write.
 */
const AGENT_AUTHORED = 'written by an AI agent';

/**
 * User vocabulary for a dropped object type (Jean-Luc round 5, F5).
 *
 * He was shown "Confirm Routine Drop" for a script touching stored procedures
 * and asked *"do you mean 'stored procedure'?"* — mid-decision, about whether
 * to destroy his own work. "Routine" is the catalog's word, not his.
 *
 * Falls back to the raw keyword for anything an engine adds later, so a new
 * `DROP EVENT`/`DROP PACKAGE` reads as "event"/"package" rather than
 * disappearing behind the generic wording.
 */
const OBJECT_TYPE_LABELS: Record<string, { one: string; many: string }> = {
  procedure: { one: 'stored procedure', many: 'stored procedures' },
  proc: { one: 'stored procedure', many: 'stored procedures' },
  view: { one: 'view', many: 'views' },
  function: { one: 'function', many: 'functions' },
  trigger: { one: 'trigger', many: 'triggers' },
  event: { one: 'event', many: 'events' },
  package: { one: 'package', many: 'packages' },
  type: { one: 'type', many: 'types' },
};

const labelFor = (keyword: string, plural: boolean): string => {
  const entry = OBJECT_TYPE_LABELS[keyword];
  if (entry) return plural ? entry.many : entry.one;
  return plural ? `${keyword}s` : keyword;
};

/**
 * "stored procedures", "views and stored procedures", "" when the verdict
 * carried no object types (an older verdict shape, or a pattern with no
 * capture group).
 */
const describeDroppedObjectTypes = (types: readonly string[] | undefined, plural: boolean): string => {
  if (!types || types.length === 0) return '';
  const labels = types.map((t) => labelFor(t, plural));
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
};

/** Title Case for the dialog heading: "stored procedure" -> "Stored Procedure". */
const titleCase = (text: string): string =>
  text.replace(/\b[a-z]/g, (c) => c.toUpperCase());

/**
 * Sentence case for the summary line: "views, stored procedures and functions"
 * -> "Views, stored procedures and functions". Title Case is wrong here — it
 * capitalises the conjunction ("Views, Stored Procedures And Functions").
 */
const sentenceCase = (text: string): string =>
  text.length === 0 ? text : text[0].toUpperCase() + text.slice(1);

function getExplanationMessage(dangerInfo: DangerousQueryResult): string {
  const { reason, severity } = dangerInfo;

  // A1 — the one verdict here that is a computed fact rather than a text
  // heuristic: the script generator compared the column's before and after
  // shapes and knows the stored values will be rewritten. Say what is lost.
  if (dangerInfo.kind === 'column-data-loss') {
    return `This script changes a column to a type that keeps less precision than it holds today. SQL Server, PostgreSQL and MySQL all accept that without an error: every stored value is rounded in place, no rows are reported, and widening the column again does not bring the discarded digits back. The [REVIEW] lines in the script name the columns and the exact loss.`;
  }

  if (reason?.includes(AGENT_AUTHORED)) {
    return reason.includes('cannot inspect')
      ? `An AI agent in your terminal wrote this statement, and it runs SQL that I can't read ahead of time — dynamic SQL or a procedure call. I can't tell you what it will change, so please check it against the target below before allowing it.`
      : `An AI agent in your terminal wrote this statement — you didn't type it. It can overwrite or delete rows that already exist, so it needs an explicit OK from you even if you've allowed this agent to run queries for the session.`;
  }

  if (severity === 'low') {
    const named = describeDroppedObjectTypes(dangerInfo.droppedObjectTypes, true);
    const subject = named || 'database routines or views';
    return `This script drops ${subject}. Their code is removed but no rows in any table are affected. Anything that calls them breaks until they are recreated.`;
  }

  // Base messages that feel conversational
  if (reason?.includes('no WHERE clause')) {
    return `I noticed this statement doesn't have a WHERE clause, which means it will affect every row in the table. This is often unintentional and can lead to data loss that's difficult to recover from.`;
  }

  if (reason?.includes('permanently modify or delete')) {
    return `This query contains a destructive operation that will permanently alter your database structure. Once executed, this change cannot be easily undone and may affect other parts of your application.`;
  }

  if (reason?.includes('system-level command')) {
    return `This appears to be a system-level command that could impact database performance or stability. These operations typically require careful consideration and are best run during maintenance windows.`;
  }

  if (reason?.includes('Could not parse')) {
    return `I had trouble analyzing this query's structure. This sometimes happens with complex queries. I'd recommend reviewing it carefully before executing to ensure it does what you expect.`;
  }

  // Fallback for any other dangerous patterns
  return `This query could make significant changes to your database. I'd recommend double-checking that this is what you intend before proceeding.`;
}

/**
 * Get a short risk summary for the header area.
 */
function getRiskSummary(dangerInfo: DangerousQueryResult): string {
  const { reason, severity } = dangerInfo;

  if (dangerInfo.kind === 'column-data-loss') {
    return 'Stored column data will be destroyed';
  }

  if (reason?.includes(AGENT_AUTHORED)) {
    return 'Written by an AI agent';
  }

  if (severity === 'low') {
    const named = describeDroppedObjectTypes(dangerInfo.droppedObjectTypes, true);
    return named ? `${sentenceCase(named)} will be dropped` : 'Routines or views will be dropped';
  }

  if (reason?.includes('no WHERE clause')) {
    return 'Affects all rows in the table';
  }

  if (reason?.includes('permanently modify or delete')) {
    return 'Destructive operation detected';
  }

  if (reason?.includes('system-level command')) {
    return 'System-level operation';
  }

  if (reason?.includes('Could not parse')) {
    return 'Query structure unclear';
  }

  return 'Potentially dangerous operation';
}

function getDialogTitle(dangerInfo: DangerousQueryResult, severity: 'high' | 'low' | undefined): string {
  if (dangerInfo.kind === 'column-data-loss') return 'Confirm Column Data Loss';
  if (severity === 'low') {
    const types = dangerInfo.droppedObjectTypes;
    // One kind of object gets named outright. A script dropping several kinds
    // stays generic in the title and lists them in the summary line below it —
    // "Confirm Stored Procedure Drop" on a script that also drops views would
    // be a more precise lie than the old wording.
    if (types && types.length === 1) return `Confirm ${titleCase(labelFor(types[0], false))} Drop`;
    return 'Confirm Object Drop';
  }
  return 'Confirm Query Execution';
}

/**
 * The closing tip.
 *
 * Keyed on the VERDICT, not on severity (Jean-Luc round 5, F2). He ran a
 * schema-sync script — pure DDL — and was told *"You can add a WHERE clause or
 * use a transaction to make this safer"*, then asked us *"What 'You can add a
 * WHERE clause' means? Where how?"*. It was advice for a different feature: a
 * WHERE clause is the fix for exactly one verdict, `missing-where`, and for
 * nothing else. Every other kind now gets advice that applies to it.
 */
function getTip(dangerInfo: DangerousQueryResult, severity: 'high' | 'low'): string {
  if (dangerInfo.kind === 'column-data-loss') {
    return 'Tip: A transaction does not help here — once committed, the rounded values are the only ones left. Copy the affected columns to a backup table first.';
  }
  if (severity === 'low') {
    return 'Tip: Check what calls these before dropping them — a dependency search on the object name finds most callers.';
  }
  if (dangerInfo.kind === 'missing-where') {
    return 'Tip: Add a WHERE clause to limit which rows this touches, or run it inside a transaction so you can roll back.';
  }
  if (dangerInfo.kind === 'destructive-ddl') {
    return 'Tip: This changes database structure, so a WHERE clause does not apply. Read the statements below, and take a backup if the objects hold data you need.';
  }
  if (dangerInfo.kind === 'unparseable') {
    return 'Tip: Read the script through before running it — the structure could not be analysed, so nothing here is a complete account of what it does.';
  }
  if (dangerInfo.kind === 'engine-specific') {
    return 'Tip: Server-level commands affect every connection, not just yours. Prefer a maintenance window.';
  }
  return 'Tip: Check this against the target above before running it.';
}

function getConfirmButtonLabel(severity: 'high' | 'low' | undefined): string {
  if (severity === 'low') return 'Drop Anyway';
  return 'Execute Anyway';
}

export function DangerousQueryDialog({
  open,
  onConfirm,
  onCancel,
  dangerInfo,
  connectionName,
  database,
}: DangerousQueryDialogProps) {
  const explanation = getExplanationMessage(dangerInfo);
  const riskSummary = getRiskSummary(dangerInfo);
  const statementPreview = dangerInfo.statement || '';
  // Default to 'high' if upstream forgot to set it — preserves the
  // pre-tiering "always loud" behavior for unknown patterns.
  const severity: 'high' | 'low' = dangerInfo.severity ?? 'high';
  const isLow = severity === 'low';

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="dangerous-query-dialog"
        data-severity={severity}
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div
              className={
                isLow
                  ? 'flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/10'
                  : 'flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10'
              }
            >
              {isLow ? (
                <Info className="h-5 w-5 text-warning" />
              ) : (
                <ShieldAlert className="h-5 w-5 text-destructive" />
              )}
            </div>
            <div>
              <DialogTitle className="text-lg">
                {getDialogTitle(dangerInfo, severity)}
              </DialogTitle>
              <DialogDescription className="mt-1 text-sm">
                {riskSummary}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Where this lands. Never omitted — an unknown target is itself
              worth saying out loud before a destructive statement. */}
          <div
            className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs"
            data-testid="dangerous-query-target"
          >
            <span className="text-muted-foreground">Connection:</span>
            <span className="font-medium text-foreground">{connectionName || '(unknown)'}</span>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground">Database:</span>
            <span className="font-medium text-foreground">{database || '(none selected)'}</span>
          </div>

          {/* AI-like explanation */}
          <p
            className="text-sm text-foreground leading-relaxed"
            data-testid="dangerous-query-explanation"
          >
            {explanation}
          </p>

          {/* Statement preview */}
          {statementPreview && (
            <div className="rounded-md border border-border bg-muted/50 p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle
                  className={
                    isLow
                      ? 'h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground'
                      : 'h-4 w-4 mt-0.5 flex-shrink-0 text-warning'
                  }
                />
                <code
                  className="text-xs text-muted-foreground font-mono break-all"
                  data-testid="dangerous-query-preview"
                >
                  {statementPreview}
                  {statementPreview.length >= 100 && '...'}
                </code>
              </div>
            </div>
          )}

          {/* Tip */}
          <p className="text-xs text-muted-foreground" data-testid="dangerous-query-tip">
            {getTip(dangerInfo, severity)}
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={onCancel}
            data-testid="dangerous-query-cancel"
          >
            Cancel
          </Button>
          <Button
            variant={isLow ? 'default' : 'destructive'}
            onClick={onConfirm}
            data-testid="dangerous-query-confirm"
          >
            {getConfirmButtonLabel(severity)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
